import { Stop, Trash } from "@phosphor-icons/react";
import clsx from "clsx";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";

import { AUDIO_MIME_TYPES } from "../constants";
import AudioPlayer from "./AudioPlayer";
import { LiveWaveform } from "./LiveWaveform";
import TimerDisplay from "./TimerDisplay";

const MAX_RECORDING_TIME = 60;

interface VoiceRecordingProps {
  onRecordingEnd: (audioBlob?: Blob) => void;
  onRecordingDelete?: () => void;
  initialBlob?: Blob;
  isLoading?: boolean;
  isUploading?: boolean;
}

export default function VoiceRecording({
  onRecordingEnd,
  onRecordingDelete,
  initialBlob,
  isLoading,
  isUploading,
}: VoiceRecordingProps) {
  const supportedMimeType = AUDIO_MIME_TYPES.find(({ mimeType }) =>
    MediaRecorder.isTypeSupported(mimeType),
  );
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const intervalIdRef = useRef<number>();
  const chunksRef = useRef<Blob[]>([]);
  const shouldSaveOnEndRef = useRef<boolean>(false);

  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTimer, setRecordingTimer] = useState(0);

  const recordingStartTimeRef = useRef<number>(0);

  function stopRecording() {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    clearInterval(intervalIdRef.current);
  }

  useLayoutEffect(() => {
    if (!initialBlob && !isLoading) {
      setIsRecording(true);

      let isCancelled = false;
      let mediaRecorder: MediaRecorder;

      shouldSaveOnEndRef.current = true;
      navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
        if (isCancelled) {
          stream.getTracks().forEach((track) => track.stop());
        } else {
          mediaRecorder = new MediaRecorder(
            stream,
            supportedMimeType
              ? { mimeType: supportedMimeType.mimeType }
              : undefined,
          );
          mediaRecorderRef.current = mediaRecorder;

          const audioContext = new (
            window.AudioContext || window.webkitAudioContext
          )();

          const source = audioContext.createMediaStreamSource(stream);
          const analyser = audioContext.createAnalyser();

          analyserRef.current = analyser;
          source.connect(analyser);
          analyser.fftSize = 2048;

          chunksRef.current = [];

          mediaRecorder.ondataavailable = ({ data }) => {
            if (data.size > 0) {
              chunksRef.current.push(data);
            }
          };

          mediaRecorder.onstop = async () => {
            if (shouldSaveOnEndRef.current) {
              const finalBlob = new Blob(chunksRef.current, {
                type: mediaRecorder.mimeType,
              });
              console.log("on stop");
              setAudioBlob(finalBlob);
              onRecordingEnd(finalBlob);
            } else {
              onRecordingEnd();
            }

            await audioContext.close();
            stream.getTracks().forEach((track) => {
              track.stop();
            });
          };

          recordingStartTimeRef.current = Date.now();
          mediaRecorder.start(5000);
          intervalIdRef.current = window.setInterval(() => {
            const elapsedSeconds = Math.floor(
              (Date.now() - recordingStartTimeRef.current) / 1000,
            );
            setRecordingTimer(elapsedSeconds);
          }, 1000);
        }
      });

      return () => {
        isCancelled = true;
        if (mediaRecorder && mediaRecorder.state !== "inactive") {
          mediaRecorder.stop();
        }
        window.clearTimeout(intervalIdRef.current);
      };
    } else {
      shouldSaveOnEndRef.current = false;
      stopRecording();
    }

    setAudioBlob(initialBlob);
  }, [initialBlob, isLoading]);

  useEffect(() => {
    if (recordingTimer >= MAX_RECORDING_TIME) {
      stopRecording();
    }
  }, [recordingTimer]);

  return (
    <div className="flex flex-row-reverse items-center gap-x-1 lg:flex-row lg:gap-x-2">
      <div
        className={clsx([
          "flex items-center rounded-full p-2 pr-3 md:w-80 w-full max-w-80 gap-x-2",
          isRecording ? "bg-purple-500" : "bg-purple-50",
        ])}
      >
        {isRecording ? (
          <>
            <Button
              size="sm"
              intent="iconOnly"
              variant="secondary-purple"
              onClick={stopRecording}
              aria-label="stop recording"
            >
              <Stop weight="fill" />
            </Button>

            <LiveWaveform analyserRef={analyserRef} />

            <TimerDisplay className="text-white" value={recordingTimer} />
          </>
        ) : (
          <AudioPlayer audioBlob={audioBlob} isLoading={isLoading} />
        )}
      </div>
      <RenderIf condition={!isRecording}>
        <Button
          size="sm"
          intent="iconOnly"
          variant="secondary-black"
          onClick={onRecordingDelete}
          aria-label="delete recording"
          isLoading={isUploading}
          disabled={!onRecordingDelete}
        >
          <Trash />
        </Button>
      </RenderIf>
    </div>
  );
}
